import { API } from '@/config/api';
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminNavigation from '@/components/AdminNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar, ReferenceLine, ReferenceArea
} from 'recharts';
import { Calendar, RefreshCw, Layers, Bug, BarChart3 } from 'lucide-react';
import OrganismSelector from '@/components/OrganismSelector';

const TABS = [
  { id: 'algae', label: 'Algeprøver', icon: BarChart3 },
  { id: 'zooplankton', label: 'Zooplankton', icon: Bug },
  { id: 'biomass', label: 'Biomasse', icon: Layers },
];

const ZOO_COLORS = ['#0d9488', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function AdminAnalytics() {
  const [activeTab, setActiveTab] = useState('algae');

  // Shared date state — default "I år" (Jan 1 this year → today)
  const [startDate, setStartDate] = useState(() => {
    return `${new Date().getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [activePreset, setActivePreset] = useState('year');

  // === ALGAE STATE ===
  const [algaeData, setAlgaeData] = useState([]);
  const [algaeOrganisms, setAlgaeOrganisms] = useState([]);
  const [algaeLocations, setAlgaeLocations] = useState([]);
  const [algaeSites, setAlgaeSites] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [selectedOrganism, setSelectedOrganism] = useState('all');
  const hasAutoSelectedRef = useRef(false);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');
  const [thresholds, setThresholds] = useState(null);
  const [loadingAlgae, setLoadingAlgae] = useState(false);
  const [useLogScale, setUseLogScale] = useState(false);

  // === COMPARISON STATE ===
  const [comparisonYears, setComparisonYears] = useState([]);
  const [comparisonData, setComparisonData] = useState({});
  const comparisonColors = { 2025: '#0ea5e9', 2024: '#a855f7', 2023: '#f97316', 2022: '#06b6d4', 2021: '#ec4899' };

  // === ZOOPLANKTON STATE ===
  const [zooData, setZooData] = useState([]);
  const [zooOrganisms, setZooOrganisms] = useState([]);
  const [zooLocations, setZooLocations] = useState([]);
  const [zooThresholds, setZooThresholds] = useState({});
  const [selectedZooOrganism, setSelectedZooOrganism] = useState('all');
  const [selectedZooLocation, setSelectedZooLocation] = useState('all');
  const [loadingZoo, setLoadingZoo] = useState(false);

  // === BIOMASS STATE ===
  const [biomassData, setBiomassData] = useState([]);
  const [loadingBiomass, setLoadingBiomass] = useState(false);

  // Color from value + thresholds
  const getColorForValue = useCallback((value) => {
    if (!thresholds) return '#10b981';
    const val = parseFloat(value);
    if (val < thresholds.yellow) return '#10b981';
    if (val < thresholds.orange) return '#facc15';
    if (val < thresholds.red) return '#f97316';
    return '#ef4444';
  }, [thresholds]);

  // === FETCH FUNCTIONS ===
  const fetchAlgae = useCallback(async () => {
    setLoadingAlgae(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedOrganism !== 'all') params.append('organism', selectedOrganism);
      if (selectedLocation !== 'all') params.append('location', selectedLocation);
      if (selectedSite !== 'all') params.append('site', selectedSite);

      const res = await axios.get(`${API}/analytics/algae-trends?${params}`, { withCredentials: true });
      const sorted = (res.data.trends || [])
        .filter(t => t.date && t.value != null)
        .map(t => ({ ...t, timestamp: new Date(t.date).getTime(), value: parseFloat(t.value) || 0 }))
        .filter(t => !isNaN(t.timestamp))
        .sort((a, b) => a.timestamp - b.timestamp);

      setAlgaeData(sorted);
      setThresholds(res.data.thresholds || null);
      if (res.data.organisms?.length) {
        setAlgaeOrganisms(res.data.organisms);
        // Auto-select first relevant organism on initial load only
        if (selectedOrganism === 'all' && !hasAutoSelectedRef.current) {
          hasAutoSelectedRef.current = true;
          const skele = res.data.organisms.find(o => o.includes('Skeletonema'));
          if (skele) setSelectedOrganism(skele);
          else setSelectedOrganism(res.data.organisms[0]);
        }
      }
      if (res.data.locations?.length) setAlgaeLocations(res.data.locations);
      if (res.data.sites?.length) {
        setAllSites(res.data.sites);
        setAlgaeSites(res.data.sites.map(s => s.name || s));
      }
    } catch (e) { console.error('Algae fetch error:', e); }
    finally { setLoadingAlgae(false); }
  }, [startDate, endDate, selectedOrganism, selectedLocation, selectedSite]);

  const fetchZoo = useCallback(async () => {
    setLoadingZoo(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedZooOrganism !== 'all') params.append('organism', selectedZooOrganism);
      if (selectedZooLocation !== 'all') params.append('location', selectedZooLocation);

      const res = await axios.get(`${API}/analytics/zooplankton-trends?${params}`, { withCredentials: true });
      const processed = (res.data.trends || [])
        .map(t => ({ ...t, timestamp: new Date(t.date).getTime(), value: parseFloat(t.value) || 0 }))
        .filter(t => !isNaN(t.timestamp))
        .sort((a, b) => a.timestamp - b.timestamp);

      setZooData(processed);
      if (res.data.organisms?.length) setZooOrganisms(res.data.organisms);
      if (res.data.locations?.length) setZooLocations(res.data.locations);
      if (res.data.thresholds) setZooThresholds(res.data.thresholds);
    } catch (e) { console.error('Zoo fetch error:', e); }
    finally { setLoadingZoo(false); }
  }, [startDate, endDate, selectedZooOrganism, selectedZooLocation]);

  const fetchBiomass = useCallback(async () => {
    setLoadingBiomass(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await axios.get(`${API}/analytics/biomass-trends?${params}`, { withCredentials: true });
      const trends = (res.data.trends || [])
        .map(item => ({ ...item, timestamp: new Date(item.date).getTime(), biomass: parseFloat(item.total_biomass) || 0 }))
        .filter(item => !isNaN(item.timestamp))
        .sort((a, b) => a.timestamp - b.timestamp);
      setBiomassData(trends);
    } catch (e) { console.error('Biomass fetch error:', e); }
    finally { setLoadingBiomass(false); }
  }, [startDate, endDate]);

  // Fetch data when tab or dates change
  useEffect(() => {
    if (activeTab === 'algae') fetchAlgae();
    else if (activeTab === 'zooplankton') fetchZoo();
    else if (activeTab === 'biomass') fetchBiomass();
  }, [activeTab, fetchAlgae, fetchZoo, fetchBiomass]);

  // Filter sites when location changes
  useEffect(() => {
    if (selectedLocation === 'all') {
      setAlgaeSites(allSites.map(s => ({ name: s.name || s, displayName: s.area_name ? `${s.area_name} - ${s.name}` : s.name || s })));
    } else {
      const filtered = allSites.filter(s => s.area_name === selectedLocation).map(s => ({ name: s.name || s, displayName: s.name || s }));
      setAlgaeSites(filtered);
      if (selectedSite !== 'all' && !filtered.some(s => s.name === selectedSite)) setSelectedSite('all');
    }
  }, [selectedLocation, allSites]);

  // Zoo chart data
  const zooChartData = (() => {
    if (selectedZooOrganism !== 'all') return zooData;
    const byDate = {};
    zooData.forEach(t => {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date, timestamp: t.timestamp };
      byDate[t.date][t.organism] = (byDate[t.date][t.organism] || 0) + t.value;
    });
    return Object.values(byDate).sort((a, b) => a.timestamp - b.timestamp);
  })();
  const activeZooOrganisms = selectedZooOrganism !== 'all' ? [] : [...new Set(zooData.map(t => t.organism))].slice(0, 8);
  const currentZooThreshold = selectedZooOrganism !== 'all' ? zooThresholds[selectedZooOrganism] : null;

  const getZooColor = (val, species) => {
    const t = zooThresholds[species];
    if (!t) return '#10b981';
    if (t.threshold_red && val >= t.threshold_red) return '#ef4444';
    if (t.threshold_orange && val >= t.threshold_orange) return '#f97316';
    if (t.threshold_yellow && val >= t.threshold_yellow) return '#eab308';
    return '#10b981';
  };

  // Date presets
  const setDatePreset = (key) => {
    const now = new Date();
    let start;
    switch (key) {
      case 'year': start = new Date(now.getFullYear(), 0, 1); break;
      case 'lastyear':
        setStartDate(`${now.getFullYear() - 1}-01-01`);
        setEndDate(`${now.getFullYear() - 1}-12-31`);
        setActivePreset('lastyear');
        return;
      case '6m': start = new Date(now); start.setMonth(start.getMonth() - 6); break;
      case '3m': start = new Date(now); start.setMonth(start.getMonth() - 3); break;
      case '30d': start = new Date(now); start.setDate(start.getDate() - 30); break;
      default: start = new Date(now); start.setDate(start.getDate() - 90);
    }
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
    setActivePreset(key);
  };

  const toggleComparison = (year) => {
    setComparisonYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year].sort((a, b) => b - a));
  };

  // Fetch comparison data when years change
  useEffect(() => {
    if (comparisonYears.length === 0) { setComparisonData({}); return; }
    const fetchComparison = async () => {
      const currentYear = new Date(startDate).getFullYear();
      const startMonth = new Date(startDate).getMonth();
      const startDay = new Date(startDate).getDate();
      const endMonth = new Date(endDate).getMonth();
      const endDay = new Date(endDate).getDate();
      const allYearData = {};
      for (const year of comparisonYears) {
        try {
          const yStart = `${year}-${String(startMonth + 1).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
          const yEnd = `${year}-${String(endMonth + 1).padStart(2, '0')}-${String(Math.min(endDay, 28)).padStart(2, '0')}`;
          const params = new URLSearchParams({ start_date: yStart, end_date: yEnd });
          if (selectedOrganism !== 'all') params.append('organism', selectedOrganism);
          if (selectedLocation !== 'all') params.append('location', selectedLocation);
          if (selectedSite !== 'all') params.append('site', selectedSite);
          const res = await axios.get(`${API}/analytics/algae-trends?${params}`, { withCredentials: true });
          const yearDiff = currentYear - year;
          allYearData[year] = (res.data.trends || []).map(item => {
            const shifted = new Date(item.date);
            shifted.setFullYear(shifted.getFullYear() + yearDiff);
            return { ...item, timestamp: shifted.getTime(), value: parseFloat(item.value) || 0 };
          }).filter(i => !isNaN(i.timestamp)).sort((a, b) => a.timestamp - b.timestamp);
        } catch {}
      }
      setComparisonData(allYearData);
    };
    fetchComparison();
  }, [comparisonYears, startDate, endDate, selectedOrganism, selectedLocation, selectedSite]);

  const xAxisProps = {
    dataKey: 'timestamp', type: 'number', scale: 'time', domain: ['dataMin', 'dataMax'],
    stroke: '#94a3b8', tick: { fontSize: 12, fill: '#64748b' },
    tickFormatter: v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <AdminNavigation />
      <main className="flex-1 min-h-screen p-4 lg:p-6 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-1 tracking-tight">Analyse</h1>
            <p className="text-sm text-slate-600">Trender og utvikling for alge- og zooplanktondata</p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-6 w-fit shadow-sm">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-teal-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Shared date controls */}
          <Card className="border-slate-200 shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                  {[
                    { label: 'I år', key: 'year' }, { label: 'I fjor', key: 'lastyear' },
                    { label: '6 mnd', key: '6m' }, { label: '3 mnd', key: '3m' }, { label: '30 dagar', key: '30d' }
                  ].map(p => (
                    <button key={p.key} onClick={() => setDatePreset(p.key)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                        activePreset === p.key ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:bg-white/50'
                      }`}>{p.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePreset(''); }} className="bg-transparent border-none text-sm text-slate-700 focus:outline-none w-[115px]" />
                  <span className="text-slate-400">–</span>
                  <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePreset(''); }} className="bg-transparent border-none text-sm text-slate-700 focus:outline-none w-[115px]" />
                </div>

                {/* Year comparison */}
                {activeTab === 'algae' && (
                  <>
                    <div className="w-px h-6 bg-slate-200" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-500 font-medium">Samanlikn:</span>
                      {[2025, 2024, 2023, 2022].filter(y => {
                        const curYear = new Date(startDate).getFullYear();
                        return y !== curYear;
                      }).map(y => (
                        <button key={y} onClick={() => toggleComparison(y)}
                          className={`px-2 py-1 text-xs rounded-md transition-all border ${
                            comparisonYears.includes(y)
                              ? 'text-white shadow-sm border-transparent'
                              : 'text-slate-500 border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                          style={comparisonYears.includes(y) ? { backgroundColor: comparisonColors[y] } : {}}
                        >
                          {y}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ====== ALGAE TAB ====== */}
          {activeTab === 'algae' && (
            <Card className="border-slate-200 shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-bold text-slate-900">Algeprøver — Utvikling over tid</CardTitle>
                <p className="text-sm text-slate-600">Konsentrasjon per organisme og lokalitet</p>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mt-3">
                  <OrganismSelector organisms={algaeOrganisms} value={selectedOrganism} onChange={setSelectedOrganism} placeholder="Velg organisme" className="w-[280px]" />
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="w-[180px] border-slate-300 bg-white shadow-sm"><SelectValue placeholder="Velg område" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle områder</SelectItem>
                      {algaeLocations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selectedSite} onValueChange={setSelectedSite}>
                    <SelectTrigger className="w-[220px] border-slate-300 bg-white shadow-sm"><SelectValue placeholder="Velg stasjon" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle stasjonar</SelectItem>
                      {algaeSites.map(s => <SelectItem key={s.name || s} value={s.name || s}>{s.displayName || s.name || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                    <Checkbox checked={useLogScale} onCheckedChange={setUseLogScale} className="data-[state=checked]:bg-blue-600" />
                    <span>Log-skala</span>
                  </label>
                </div>
                {/* Threshold legend */}
                {thresholds && (
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs bg-slate-50 rounded-lg p-2.5">
                    <span className="text-slate-500 font-medium">Grenseverdiar:</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-green-500 rounded-full" /> Grøn: 0–{thresholds.yellow?.toLocaleString('nb-NO')}</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#facc15' }} /> Gul: {thresholds.yellow?.toLocaleString('nb-NO')}–{thresholds.orange?.toLocaleString('nb-NO')}</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#f97316' }} /> Oransje: {thresholds.orange?.toLocaleString('nb-NO')}–{thresholds.red?.toLocaleString('nb-NO')}</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded-full" /> Raud: &gt;{thresholds.red?.toLocaleString('nb-NO')}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loadingAlgae ? (
                  <div className="flex items-center justify-center h-80"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" /></div>
                ) : algaeData.length === 0 ? (
                  <div className="flex items-center justify-center h-80 text-slate-500">Ingen data for valgte filter</div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={algaeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis {...xAxisProps} />
                      <YAxis scale={useLogScale ? 'log' : 'auto'}
                        domain={useLogScale ? [1, 'auto'] : (thresholds ? [0, Math.max(thresholds.red * 1.15, Math.max(...algaeData.map(d => d.value || 0)))] : ['auto', 'auto'])}
                        stroke="#94a3b8" tick={{ fontSize: 12, fill: '#64748b' }}
                        tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(0)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}k` : v.toLocaleString('nb-NO')} />
                      <Tooltip contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '12px', padding: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        labelFormatter={v => new Date(v).toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        formatter={(value) => [`${value.toLocaleString('nb-NO')} ant/l`, 'Konsentrasjon']} />
                      {thresholds && !useLogScale && (
                        <>
                          <ReferenceArea y1={0} y2={thresholds.yellow} fill="#22c55e" fillOpacity={0.08} />
                          <ReferenceArea y1={thresholds.yellow} y2={thresholds.orange} fill="#fde047" fillOpacity={0.12} />
                          <ReferenceArea y1={thresholds.orange} y2={thresholds.red} fill="#fb923c" fillOpacity={0.12} />
                          <ReferenceArea y1={thresholds.red} y2={thresholds.red * 1.2} fill="#ef4444" fillOpacity={0.12} />
                          <ReferenceLine y={thresholds.yellow} stroke="#fde047" strokeWidth={2} strokeDasharray="5 5" />
                          <ReferenceLine y={thresholds.orange} stroke="#fb923c" strokeWidth={2} strokeDasharray="5 5" />
                          <ReferenceLine y={thresholds.red} stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" />
                        </>
                      )}
                      <Line type="monotone" dataKey="value" stroke="#cbd5e1" strokeWidth={2} name="Konsentrasjon"
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          if (!payload) return null;
                          return <circle cx={cx} cy={cy} r={6} fill={getColorForValue(payload.value)} stroke="#fff" strokeWidth={2} />;
                        }}
                        isAnimationActive={false} />
                      {/* Comparison year lines */}
                      {Object.entries(comparisonData).map(([year, data]) => (
                        <Line key={year} type="monotone" data={data} dataKey="value"
                          stroke={comparisonColors[year] || '#94a3b8'} strokeWidth={1.5}
                          strokeDasharray="6 3" name={year} dot={false} isAnimationActive={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* ====== ZOOPLANKTON TAB ====== */}
          {activeTab === 'zooplankton' && (
            <Card className="border-slate-200 shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-bold text-slate-900">
                  {selectedZooOrganism !== 'all' ? selectedZooOrganism : 'Zooplankton'} — Utvikling
                </CardTitle>
                <div className="flex flex-wrap gap-3 mt-3">
                  <Select value={selectedZooOrganism} onValueChange={setSelectedZooOrganism}>
                    <SelectTrigger data-testid="zoo-organism-select" className="w-[200px] border-slate-300 bg-white shadow-sm"><SelectValue placeholder="Velg organisme" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle organismar</SelectItem>
                      {zooOrganisms.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selectedZooLocation} onValueChange={setSelectedZooLocation}>
                    <SelectTrigger className="w-[180px] border-slate-300 bg-white shadow-sm"><SelectValue placeholder="Velg stasjon" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle stasjonar</SelectItem>
                      {zooLocations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {zooOrganisms.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {zooOrganisms.slice(0, 10).map(o => (
                      <button key={o} onClick={() => setSelectedZooOrganism(selectedZooOrganism === o ? 'all' : o)}
                        className={`px-2.5 py-1 text-xs rounded-md transition-colors ${selectedZooOrganism === o ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{o}</button>
                    ))}
                  </div>
                )}
                {currentZooThreshold && (
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-xs bg-slate-50 rounded-lg p-2.5">
                    <span className="text-slate-500 font-medium">Grenseverdiar:</span>
                    {currentZooThreshold.threshold_yellow && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /> Gul: {currentZooThreshold.threshold_yellow.toLocaleString('nb-NO')}</span>}
                    {currentZooThreshold.threshold_orange && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400" /> Oransje: {currentZooThreshold.threshold_orange.toLocaleString('nb-NO')}</span>}
                    {currentZooThreshold.threshold_red && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Raud: {currentZooThreshold.threshold_red.toLocaleString('nb-NO')}</span>}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loadingZoo ? (
                  <div className="flex items-center justify-center h-80"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" /></div>
                ) : zooChartData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-80 text-slate-500">
                    <Bug className="w-12 h-12 mb-3 text-slate-300" />
                    <p className="font-medium">Ingen zooplanktondata funne</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={zooChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis {...xAxisProps} />
                      <YAxis stroke="#94a3b8" tick={{ fontSize: 12, fill: '#64748b' }}
                        tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toLocaleString('nb-NO')} />
                      <Tooltip contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '12px', padding: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        labelFormatter={v => new Date(v).toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        formatter={(value, name) => [`${value.toLocaleString('nb-NO')} ant/L`, name]} />
                      <Legend />
                      {currentZooThreshold?.threshold_yellow && <ReferenceLine y={currentZooThreshold.threshold_yellow} stroke="#eab308" strokeDasharray="5 5" />}
                      {currentZooThreshold?.threshold_orange && <ReferenceLine y={currentZooThreshold.threshold_orange} stroke="#f97316" strokeDasharray="5 5" />}
                      {currentZooThreshold?.threshold_red && <ReferenceLine y={currentZooThreshold.threshold_red} stroke="#ef4444" strokeDasharray="5 5" />}
                      {selectedZooOrganism !== 'all' ? (
                        <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} name={selectedZooOrganism}
                          dot={(props) => { const { cx, cy, payload } = props; if (!payload) return null; return <circle cx={cx} cy={cy} r={6} fill={getZooColor(payload.value, selectedZooOrganism)} stroke="#fff" strokeWidth={2} />; }}
                          isAnimationActive={false} />
                      ) : (
                        activeZooOrganisms.map((org, i) => (
                          <Line key={org} type="monotone" dataKey={org} stroke={ZOO_COLORS[i % ZOO_COLORS.length]} strokeWidth={2} name={org} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
                        ))
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* ====== BIOMASS TAB ====== */}
          {activeTab === 'biomass' && (
            <Card className="border-slate-200 shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-bold text-slate-900">Biomasse over tid</CardTitle>
                <p className="text-sm text-slate-600">Total estimert biomasse basert på celletelling x biovolum</p>
              </CardHeader>
              <CardContent>
                {loadingBiomass ? (
                  <div className="flex items-center justify-center h-80"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" /></div>
                ) : biomassData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-80 text-slate-500">
                    <Layers className="w-12 h-12 mb-3 text-slate-300" />
                    <p className="font-medium">Ingen biomasse-data funne</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={biomassData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="biomassGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis {...xAxisProps} />
                      <YAxis stroke="#94a3b8" tick={{ fontSize: 12, fill: '#64748b' }}
                        tickFormatter={v => v >= 1e9 ? `${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v.toFixed(0)}
                        label={{ value: 'Biomasse (µm³/L)', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 12 } }} />
                      <Tooltip contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '12px', padding: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        labelFormatter={v => new Date(v).toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' })}
                        formatter={(value) => [`${value.toLocaleString()} µm³/L`, 'Biomasse']} />
                      <Area type="monotone" dataKey="biomass" stroke="#10b981" strokeWidth={2} fill="url(#biomassGrad)" name="Biomasse" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
